import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Button from '../../components/base/Button';
import LanguageSelector from '../../components/common/LanguageSelector';
import ReviewSubmission from '../../components/feature/ReviewSubmission';
import ReviewCard from '../../components/feature/ReviewCard';
import { useTimer } from '../../hooks/useTimer';
import { getSEOConfig, updateSEOTags } from '../../i18n/utils/seo';
import type { SupportedLanguage } from '../../i18n/utils/language';

export default function Timer() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['timer', 'common']);
  const [editingHours, setEditingHours] = useState(false);
  const [editingMinutes, setEditingMinutes] = useState(false);
  const [editingSeconds, setEditingSeconds] = useState(false);
  const [tempHours, setTempHours] = useState('00');
  const [tempMinutes, setTempMinutes] = useState('00');
  const [tempSeconds, setTempSeconds] = useState('00');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  
  // 音频控制相关的refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioTimeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // 停止所有音频播放
  const stopAllAudio = () => {
    // 清除所有音频超时
    audioTimeoutsRef.current.forEach(timeout => clearTimeout(timeout));
    audioTimeoutsRef.current = [];
    
    // 关闭AudioContext
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    // 停止HTML Audio元素
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
  };


  // 随机排序用户评价
  const [randomizedReviews, setRandomizedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
  }>>([]);

  // 用户提交的评价
  const [userSubmittedReviews, setUserSubmittedReviews] = useState<Array<{
    id: number;
    avatar: string;
    name: string;
    rating: number;
    review: string;
    isNew: boolean;
  }>>([]);

  useEffect(() => {
    // 创建用户评价数据，使用当前语言的翻译
    const userReviews = [
      {
        id: 1,
        avatar: "/images/avatars/Mike Zhang.jpg",
        name: t('user.mikeZhang'),
        rating: 5,
        review: t('reviews.mikeZhang')
      },
      {
        id: 2,
        avatar: "/images/avatars/Lisa Lee.jpg",
        name: t('user.lisaLee'),
        rating: 5,
        review: t('reviews.lisaLee')
      },
      {
        id: 3,
        avatar: "/images/avatars/John Developer.jpg",
        name: t('user.johnDeveloper'),
        rating: 5,
        review: t('reviews.johnDeveloper')
      },
      {
        id: 4,
        avatar: "/images/avatars/Ms. Chen.jpg",
        name: t('user.msChen'),
        rating: 5,
        review: t('reviews.msChen')
      },
      {
        id: 5,
        avatar: "/images/avatars/Fitness Pro.jpg",
        name: t('user.fitnessPro'),
        rating: 5,
        review: t('reviews.fitnessPro')
      },
      {
        id: 6,
        avatar: "/images/avatars/Sarah Marketer.jpg",
        name: t('user.sarahMarketer'),
        rating: 5,
        review: t('reviews.sarahMarketer')
      },
      {
        id: 7,
        avatar: "/images/avatars/Tom Chef.jpg",
        name: t('user.tomChef'),
        rating: 5,
        review: t('reviews.tomChef')
      },
      {
        id: 8,
        avatar: "/images/avatars/Amy Student.jpg",
        name: t('user.amyStudent'),
        rating: 4,
        review: t('reviews.amyStudent')
      },
      {
        id: 9,
        avatar: "/images/avatars/David Writer.jpg",
        name: t('user.davidWriter'),
        rating: 5,
        review: t('reviews.davidWriter')
      },
      {
        id: 10,
        avatar: "/images/avatars/Rachel Therapist.jpg",
        name: t('user.rachelTherapist'),
        rating: 5,
        review: t('reviews.rachelTherapist')
      }
    ];

    // 只在语言改变时重新生成和随机排序，但保持同一语言下的稳定性
    const shuffled = [...userReviews].sort(() => Math.random() - 0.5);
    setRandomizedReviews(shuffled);
  }, [t, i18n.language]); // 依赖翻译函数和语言

  // 组件卸载时清理音频
  useEffect(() => {
    return () => {
      stopAllAudio();
    };
  }, []);

  // 计算节日倒计时
  const getUpcomingHolidays = () => {
    const now = new Date();
    const currentYear = now.getFullYear();

    const holidays = [
      { name: { en: 'New Year', zh: '元旦' }, month: 0, day: 1 },
      { name: { en: 'Valentine\'s Day', zh: '情人节' }, month: 1, day: 14 },
      { name: { en: 'Women\'s Day', zh: '妇女节' }, month: 2, day: 8 },
      { name: { en: 'April Fool\'s Day', zh: '愚人节' }, month: 3, day: 1 },
      { name: { en: 'Labor Day', zh: '劳动节' }, month: 4, day: 1 },
      { name: { en: 'Children\'s Day', zh: '儿童节' }, month: 5, day: 1 },
      { name: { en: 'Halloween', zh: '万圣节' }, month: 9, day: 31 },
      { name: { en: 'Christmas', zh: '圣诞节' }, month: 11, day: 25 }
    ];

    const holidaysWithCountdown = holidays.map(holiday => {
      let holidayDate = new Date(currentYear, holiday.month, holiday.day);

      // 如果今年的节日已过，计算明年的
      if (holidayDate < now) {
        holidayDate = new Date(currentYear + 1, holiday.month, holiday.day);
      }

      const diffTime = holidayDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      const diffMinutes = Math.floor(diffTime / (1000 * 60));

      return {
        ...holiday,
        date: holidayDate,
        daysLeft: diffDays,
        hoursLeft: diffHours,
        minutesLeft: diffMinutes,
        displayName: i18n.language === 'zh' ? holiday.name.zh : holiday.name.en
      };
    });

    // 按距离当前时间排序，返回最近的6个
    return holidaysWithCountdown.sort((a, b) => a.daysLeft - b.daysLeft).slice(0, 6);
  };

  const upcomingHolidays = getUpcomingHolidays();

  useEffect(() => {
    // 更新SEO配置
    const currentLang = i18n.language as SupportedLanguage;
    const seoConfig = getSEOConfig('timer', currentLang);
    updateSEOTags(seoConfig, currentLang);
    
    // 请求通知权限
    requestNotificationPermission();
  }, [i18n.language]);

  const { time, isRunning, canStart, start, pause, reset, setTime } = useTimer({
    initialTime: 0,
    t,
    language: i18n.language,
    soundEnabled,
    onComplete: () => {
      // Show completion modal
      setShowCompletionModal(true);
      
      // Desktop notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(t('common:message.timerComplete'), {
          body: t('common:message.timerCompleteDesc'),
          icon: '/favicon.png'
        });
      }
      
      // Alarm sound reminder - longer and more noticeable alert tone
      if (soundEnabled) {
        // Create multiple audio segments to simulate alarm effect
        const playAlarm = () => {
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioContext;
          
          // Create alarm sound: continuous beeping
          const createBeep = (frequency: number, duration: number, delay: number) => {
            const timeout = setTimeout(() => {
              if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
                oscillator.type = 'sine';
                
                gainNode.gain.setValueAtTime(0, audioContext.currentTime);
                gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
                
                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + duration);
              }
            }, delay);
            
            // 记录timeout以便取消
            audioTimeoutsRef.current.push(timeout);
          };
          
          // Play continuous alarm sound - high and low pitch alternating, lasting 5 seconds
          for (let i = 0; i < 10; i++) {
            const frequency = i % 2 === 0 ? 800 : 600; // Alternating high and low pitch
            createBeep(frequency, 0.4, i * 500); // Every 0.5 seconds, each lasting 0.4 seconds
          }
        };
        
        // If AudioContext is not available, use backup audio file
        try {
          playAlarm();
        } catch (error) {
          // Backup plan: use audio file
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwgBS2Gy/LYeSkELYTO8tuGMwgeZ7bv6qBVEQpKoeDwt2wcAT6Mz/LWgiMFJH3J8daWRAwUYrPm7K1YEwlHmN/wul0fBSuTzvLWfSMFKX3K8daWRAweY7nr6qJZEQpInd7wuVAiAj2TzfLWfyUF');
          audio.volume = 0.8;
          audioRef.current = audio;
          
          // Play multiple times in a row
          const playCount = 5;
          let currentPlay = 0;
          
          const playNext = () => {
            if (currentPlay < playCount && audioRef.current) {
              audioRef.current.currentTime = 0;
              audioRef.current.play().then(() => {
                currentPlay++;
                const timeout = setTimeout(playNext, 800); // Play next after 0.8 seconds interval
                audioTimeoutsRef.current.push(timeout);
              }).catch(() => {
                // Ignore playback errors
              });
            }
          };
          
          playNext();
        }
      }
    }
  });

  // Get current time hours and minutes
  const totalMs = Math.abs(time);
  const hours = Math.floor(totalMs / 3600000);
  const minutes = Math.floor((totalMs % 3600000) / 60000);
  const seconds = Math.floor((totalMs % 60000) / 1000);

  const handleHoursClick = () => {
    if (!isRunning) {
      setEditingHours(true);
      setTempHours(hours.toString().padStart(2, '0'));
    }
  };

  const handleMinutesClick = () => {
    if (!isRunning) {
      setEditingMinutes(true);
      setTempMinutes(minutes.toString().padStart(2, '0'));
    }
  };

  const handleSecondsClick = () => {
    if (!isRunning) {
      setEditingSeconds(true);
      setTempSeconds(seconds.toString().padStart(2, '0'));
    }
  };

  const handleHoursChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.min(Math.max(numValue, 0), 23); // Hour limit 23
    setTempHours(clampedValue.toString().padStart(2, '0'));
  };

  const handleMinutesChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.min(Math.max(numValue, 0), 59); // Minute limit 59
    setTempMinutes(clampedValue.toString().padStart(2, '0'));
  };

  const handleSecondsChange = (value: string) => {
    const numValue = parseInt(value) || 0;
    const clampedValue = Math.min(Math.max(numValue, 0), 59); // Second limit 59
    setTempSeconds(clampedValue.toString().padStart(2, '0'));
  };

  const handleHoursSubmit = () => {
    const newHours = parseInt(tempHours) || 0;
    const newTime = (newHours * 3600 + minutes * 60 + seconds) * 1000;
    setTime(newTime);
    setEditingHours(false);
  };

  const handleMinutesSubmit = () => {
    const newMinutes = parseInt(tempMinutes) || 0;
    const newTime = (hours * 3600 + newMinutes * 60 + seconds) * 1000;
    setTime(newTime);
    setEditingMinutes(false);
  };

  const handleSecondsSubmit = () => {
    const newSeconds = parseInt(tempSeconds) || 0;
    const newTime = (hours * 3600 + minutes * 60 + newSeconds) * 1000;
    setTime(newTime);
    setEditingSeconds(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'hours' | 'minutes' | 'seconds') => {
    if (e.key === 'Enter') {
      if (type === 'hours') {
        handleHoursSubmit();
      } else if (type === 'minutes') {
        handleMinutesSubmit();
      } else if (type === 'seconds') {
        handleSecondsSubmit();
      }
    } else if (e.key === 'Escape') {
      setEditingHours(false);
      setEditingMinutes(false);
      setEditingSeconds(false);
    }
  };

  const addTime = (minutes: number) => {
    const newTime = time + (minutes * 60 * 1000);
    setTime(newTime);
  };

  const addSeconds = (seconds: number) => {
    const newTime = time + (seconds * 1000);
    setTime(newTime);
  };

  const requestNotificationPermission = () => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleFullscreenKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFullscreen(false);
    }
  };

  const handleCompletionModalClose = () => {
    setShowCompletionModal(false);
    // 停止所有音频播放
    stopAllAudio();
  };

  // 处理用户提交评价
  const handleReviewSubmit = (reviewData: { rating: number; comment: string; name: string }) => {
    const newReview = {
      id: Date.now(), // 使用时间戳作为ID
      avatar: "/images/avatars/default.jpg", // 默认头像
      name: reviewData.name,
      rating: reviewData.rating,
      review: reviewData.comment || "用户没有留下具体评价，但给出了评分。",
      isNew: true
    };

    // 添加到用户提交的评价列表
    setUserSubmittedReviews(prev => [newReview, ...prev]);
  };

  // Fullscreen modal
  if (isFullscreen) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center"
        onClick={toggleFullscreen}
        onKeyDown={handleFullscreenKeyPress}
        tabIndex={0}
      >
        <div className="text-center" onClick={(e) => e.stopPropagation()}>
          {/* Exit button */}
          <button
            onClick={toggleFullscreen}
            className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          
          {/* Large time display */}
          <div className="flex items-center justify-center space-x-4 mb-12">
            {/* Hour display - only show when hours > 0 or editing */}
            {(hours > 0 || editingHours) && (
              <>
                <div className="flex flex-col items-center">
                  {editingHours ? (
                    <input
                      type="number"
                      value={tempHours}
                      onChange={(e) => handleHoursChange(e.target.value)}
                      onBlur={handleHoursSubmit}
                      onKeyDown={(e) => handleKeyPress(e, 'hours')}
                      className="text-8xl md:text-9xl font-mono font-[700] text-white bg-transparent border-b-4 border-blue-300 text-center w-40 md:w-48 focus:outline-none focus:border-blue-500"
                      autoFocus
                      min="0"
                      max="23"
                    />
                  ) : (
                    <button
                      onClick={handleHoursClick}
                      className="text-8xl md:text-9xl font-mono font-[700] text-white hover:text-blue-300 cursor-pointer transition-colors w-40 md:w-48 text-center"
                      disabled={isRunning}
                    >
                      {hours.toString().padStart(2, '0')}
                    </button>
                  )}
                  <span className="text-lg text-gray-300 mt-4">{t('common:label.hours')}</span>
                </div>
                
                <div className="flex items-center justify-center h-48">
                  <span className="text-8xl md:text-9xl font-mono font-[700] text-gray-400">:</span>
                </div>
              </>
            )}
            
            {/* Minutes display */}
            <div className="flex flex-col items-center">
              {editingMinutes ? (
                <input
                  type="number"
                  value={tempMinutes}
                  onChange={(e) => handleMinutesChange(e.target.value)}
                  onBlur={handleMinutesSubmit}
                  onKeyDown={(e) => handleKeyPress(e, 'minutes')}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white bg-transparent border-b-4 border-blue-300 text-center w-40 md:w-48 focus:outline-none focus:border-blue-500"
                  autoFocus
                  min="0"
                  max="59"
                />
              ) : (
                <button
                  onClick={handleMinutesClick}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white hover:text-blue-300 cursor-pointer transition-colors w-40 md:w-48 text-center"
                  disabled={isRunning}
                >
                  {minutes.toString().padStart(2, '0')}
                </button>
              )}
              <span className="text-lg text-gray-300 mt-4">{t('common:label.minutes')}</span>
            </div>
            
            <div className="flex items-center justify-center h-48">
              <span className="text-8xl md:text-9xl font-mono font-[700] text-gray-400">:</span>
            </div>
            
            {/* Seconds display - now editable */}
            <div className="flex flex-col items-center">
              {editingSeconds ? (
                <input
                  type="number"
                  value={tempSeconds}
                  onChange={(e) => handleSecondsChange(e.target.value)}
                  onBlur={handleSecondsSubmit}
                  onKeyDown={(e) => handleKeyPress(e, 'seconds')}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white bg-transparent border-b-4 border-blue-300 text-center w-40 md:w-48 focus:outline-none focus:border-blue-500"
                  autoFocus
                  min="0"
                  max="59"
                />
              ) : (
                <button
                  onClick={handleSecondsClick}
                  className="text-8xl md:text-9xl font-mono font-[700] text-white hover:text-blue-300 cursor-pointer transition-colors w-40 md:w-48 text-center"
                  disabled={isRunning}
                >
                  {seconds.toString().padStart(2, '0')}
                </button>
              )}
              <span className="text-lg text-gray-300 mt-4">{t('common:label.seconds')}</span>
            </div>
          </div>
          
          {/* Control buttons */}
          <div className="flex items-center justify-center space-x-6">
            {!isRunning ? (
              <Button 
                onClick={start} 
                size="lg" 
                className="px-8 py-4 text-xl" 
                disabled={!canStart}
              >
                <i className="ri-play-fill mr-3 text-xl"></i>
                {t('common:button.start')}
              </Button>
            ) : (
              <Button onClick={pause} variant="secondary" size="lg" className="px-8 py-4 text-xl">
                <i className="ri-pause-fill mr-3 text-xl"></i>
                {t('common:button.pause')}
              </Button>
            )}
            <Button onClick={reset} variant="danger" size="lg" className="px-8 py-4 text-xl">
              <i className="ri-refresh-line mr-3 text-xl"></i>
              {t('common:button.reset')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-4 md:py-8">
        {/* Main countdown display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="text-center">
            <div className="flex justify-between items-start mb-6 md:mb-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">{t('page.title')}</h2>
              
              {/* Sound switch and fullscreen button - moved to top right */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="hidden md:block text-sm text-gray-600">{t('common:label.soundAlert')}</span>
                  <span className="md:hidden text-sm text-gray-600">{t('common:label.sound')}</span>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        soundEnabled ? 'transform translate-x-7' : 'transform translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                
                <button
                  onClick={toggleFullscreen}
                  className="text-gray-600 hover:text-blue-600 transition-colors p-2 hover:bg-gray-100 rounded-lg flex items-center space-x-2"
                  title="Fullscreen"
                >
                  <span className="hidden md:block text-sm font-medium">{t('common:label.fullscreen')}</span>
                  <i className="ri-picture-in-picture-2-line text-xl"></i>
                </button>
              </div>
            </div>
            
            {/* Time display container - unified height structure */}
            <div className="mb-8 md:mb-10">
              <div className="flex items-center justify-center space-x-1 md:space-x-2 mb-6 md:mb-8">
                {/* Hour display - only show when hours > 0 or editing */}
                {(hours > 0 || editingHours) && (
                  <>
                    <div className="flex flex-col items-center">
                      {editingHours ? (
                        <input
                          type="number"
                          value={tempHours}
                          onChange={(e) => handleHoursChange(e.target.value)}
                          onBlur={handleHoursSubmit}
                          onKeyDown={(e) => handleKeyPress(e, 'hours')}
                          className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 bg-transparent border-b-4 border-blue-300 text-center w-24 md:w-32 focus:outline-none focus:border-blue-500"
                          autoFocus
                          min="0"
                          max="23"
                        />
                      ) : (
                        <button
                          onClick={handleHoursClick}
                          className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 hover:text-blue-700 cursor-pointer transition-colors w-24 md:w-32 text-center"
                          disabled={isRunning}
                        >
                          {hours.toString().padStart(2, '0')}
                        </button>
                      )}
                      <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.hours')}</span>
                    </div>
                    
                    <div className="flex items-center justify-center h-20 md:h-32">
                      <span className="text-6xl md:text-8xl font-mono font-[700] text-gray-400">:</span>
                    </div>
                  </>
                )}
                
                {/* Minutes display */}
                <div className="flex flex-col items-center">
                  {editingMinutes ? (
                    <input
                      type="number"
                      value={tempMinutes}
                      onChange={(e) => handleMinutesChange(e.target.value)}
                      onBlur={handleMinutesSubmit}
                      onKeyDown={(e) => handleKeyPress(e, 'minutes')}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 bg-transparent border-b-4 border-blue-300 text-center w-24 md:w-32 focus:outline-none focus:border-blue-500"
                      autoFocus
                      min="0"
                      max="59"
                    />
                  ) : (
                    <button
                      onClick={handleMinutesClick}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 hover:text-blue-700 cursor-pointer transition-colors w-24 md:w-32 text-center"
                      disabled={isRunning}
                    >
                      {minutes.toString().padStart(2, '0')}
                    </button>
                  )}
                  <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.minutes')}</span>
                </div>
                
                <div className="flex items-center justify-center h-20 md:h-32">
                  <span className="text-6xl md:text-8xl font-mono font-[700] text-gray-400">:</span>
                </div>
                
                {/* Seconds display - now editable */}
                <div className="flex flex-col items-center">
                  {editingSeconds ? (
                    <input
                      type="number"
                      value={tempSeconds}
                      onChange={(e) => handleSecondsChange(e.target.value)}
                      onBlur={handleSecondsSubmit}
                      onKeyDown={(e) => handleKeyPress(e, 'seconds')}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 bg-transparent border-b-4 border-blue-300 text-center w-24 md:w-32 focus:outline-none focus:border-blue-500"
                      autoFocus
                      min="0"
                      max="59"
                    />
                  ) : (
                    <button
                      onClick={handleSecondsClick}
                      className="text-6xl md:text-8xl font-mono font-[700] text-blue-600 hover:text-blue-700 cursor-pointer transition-colors w-24 md:w-32 text-center"
                      disabled={isRunning}
                    >
                      {seconds.toString().padStart(2, '0')}
                    </button>
                  )}
                  <span className="text-xs md:text-sm text-gray-500 mt-1 md:mt-2 font-medium">{t('common:label.seconds')}</span>
                </div>
              </div>
              
              {!isRunning && (
                <div className="text-center mb-4 md:mb-6">
                  <p className="text-gray-500 text-sm">{t('common:message.clickToEdit')}</p>
                  {hours === 0 && !editingHours && (
                    <button
                      onClick={handleHoursClick}
                      className="text-blue-600 hover:text-blue-700 text-sm underline mt-1"
                    >
{t('common:message.addHours')}
                    </button>
                  )}
                </div>
              )}
              
              {/* Quick add time buttons */}
              <div className="grid grid-cols-2 md:flex md:items-center md:justify-center gap-2 md:gap-3 mb-6 md:mb-8">
                <Button onClick={() => addSeconds(30)} variant="secondary" size="sm" className="whitespace-nowrap">
                  {t('common:quick.add30s')}
                </Button>
                <Button onClick={() => addTime(1)} variant="secondary" size="sm" className="whitespace-nowrap">
                  {t('common:quick.add1min')}
                </Button>
                <Button onClick={() => addTime(5)} variant="secondary" size="sm" className="whitespace-nowrap">
                  {t('common:quick.add5min')}
                </Button>
                <Button onClick={() => addTime(10)} variant="secondary" size="sm" className="whitespace-nowrap">
                  {t('common:quick.add10min')}
                </Button>
                <Button onClick={() => addTime(30)} variant="secondary" size="sm" className="whitespace-nowrap">
                  {t('common:quick.add30min')}
                </Button>
              </div>

              {/* Control buttons */}
              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto">
                {!isRunning ? (
                  <Button 
                    onClick={start} 
                    size="md" 
                    className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap" 
                    disabled={!canStart}
                  >
                    <i className="ri-play-fill mr-2"></i>
                    {t('common:button.start')}
                  </Button>
                ) : (
                  <Button onClick={pause} variant="secondary" size="md" className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap">
                    <i className="ri-pause-fill mr-2"></i>
                    {t('common:button.pause')}
                  </Button>
                )}
                <Button onClick={reset} variant="danger" size="md" className="px-4 py-3 md:px-6 md:py-4 w-full whitespace-nowrap">
                  <i className="ri-refresh-line mr-2"></i>
                  {t('common:button.reset')}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Popular timers */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('popular.title')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-3">
            {[
              { label: t('popular.10s'), path: '/timer/10-seconds-timer' },
              { label: t('popular.20s'), path: '/timer/20-seconds-timer' },
              { label: t('popular.30s'), path: '/timer/30-seconds-timer' },
              { label: t('popular.40s'), path: '/timer/40-seconds-timer' },
              { label: t('popular.50s'), path: '/timer/50-seconds-timer' },
              { label: t('popular.90s'), path: '/timer/90-seconds-timer' },
              { label: t('popular.2minute'), path: '/timer/2-minute-timer' },
              { label: t('popular.3minute'), path: '/timer/3-minute-timer' },
              { label: t('popular.5minute'), path: '/timer/5-minute-timer' },
              { label: t('popular.10minute'), path: '/timer/10-minute-timer' },
              { label: t('popular.15minute'), path: '/timer/15-minute-timer' },
              { label: t('popular.30minute'), path: '/timer/30-minute-timer' },
              { label: t('popular.60minute'), path: '/timer/60-minute-timer' },
              { label: t('popular.1hour'), path: '/timer/1-hour-timer' },
              { label: t('popular.10hour'), path: '/timer/10-hour-timer' },
              { label: t('popular.24hour'), path: '/timer/24-hour-timer' },
              { label: t('popular.48hour'), path: '/timer/48-hour-timer' }
            ].map((timer, index) => (
              <button
                key={index}
                onClick={() => {
                  navigate(timer.path);
                }}
                className="text-blue-600 hover:text-blue-800 hover:underline transition-colors cursor-pointer text-sm md:text-base text-left"
              >
                {timer.label}
              </button>
            ))}
          </div>
        </div>

        {/* Holiday Countdown */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">
            {i18n.language === 'zh' ? '节日倒计时' : 'Holiday Countdown'}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {upcomingHolidays.map((holiday, index) => (
              <button
                key={index}
                onClick={() => {
                  // 设置倒计时到该节日
                  const timeInMs = holiday.minutesLeft * 60 * 1000;
                  setTime(timeInMs);
                }}
                className="bg-gradient-to-br from-blue-50 to-purple-50 hover:from-blue-100 hover:to-purple-100 rounded-xl p-4 transition-all duration-200 hover:shadow-md border border-blue-100 text-left"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <i className="ri-calendar-event-line text-purple-600 text-xl"></i>
                    <h4 className="font-semibold text-gray-800 text-sm md:text-base">{holiday.displayName}</h4>
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-2xl font-bold text-blue-600">
                    {holiday.daysLeft} {i18n.language === 'zh' ? '天' : 'days'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {holiday.hoursLeft} {i18n.language === 'zh' ? '小时' : 'hours'} / {holiday.minutesLeft.toLocaleString()} {i18n.language === 'zh' ? '分钟' : 'minutes'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* How to use */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('howto.title')}</h3>
          <div className="space-y-3 md:space-y-4 text-gray-600">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">1</div>
              <p className="text-sm md:text-base">{t('howto.step1')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">2</div>
              <p className="text-sm md:text-base">{t('howto.step2')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">3</div>
              <p className="text-sm md:text-base">{t('howto.step3')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">4</div>
              <p className="text-sm md:text-base">{t('howto.step4')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">5</div>
              <p className="text-sm md:text-base">{t('howto.step5')}</p>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full text-sm font-bold mt-0.5 flex-shrink-0">6</div>
              <p className="text-sm md:text-base">{t('howto.step6')}</p>
            </div>
          </div>
        </div>

        {/* Review submission form */}
        <ReviewSubmission onSubmit={handleReviewSubmit} />

        {/* User reviews display */}
        <div className="bg-white rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('reviews.title')}</h3>
          <div className="relative overflow-hidden">
            <div className="flex overflow-x-auto space-x-4 md:space-x-8 scrollbar-hide">
              {/* 用户提交的评价 (优先显示) */}
              {userSubmittedReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  id={review.id}
                  avatar={review.avatar}
                  name={review.name}
                  rating={review.rating}
                  review={review.review}
                  isNew={review.isNew}
                />
              ))}
              
              {/* 现有的用户评价 */}
              {randomizedReviews.map((review) => (
                <ReviewCard
                  key={review.id}
                  id={review.id}
                  avatar={review.avatar}
                  name={review.name}
                  rating={review.rating}
                  review={review.review}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-100 text-gray-800 rounded-2xl p-6 md:p-8">
          <div className="flex flex-col lg:flex-row justify-between items-start space-y-6 lg:space-y-0">
            {/* Left side - Website description */}
            <div className="lg:w-2/3">
              <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4 md:mb-6">{t('common:footer.about')}</h4>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                {t('common:footer.aboutDesc')}
              </p>
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start space-x-3">
                  <i className="ri-timer-line text-blue-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('page.title')}</span> - {t('common:footer.timerDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-play-circle-line text-green-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.stopwatch')}</span> - {t('common:footer.stopwatchDesc')}
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="ri-global-line text-purple-500 mt-0.5 flex-shrink-0"></i>
                  <div>
                    <span className="font-medium text-gray-900">{t('common:nav.worldclock')}</span> - {t('common:footer.worldClockDesc')}
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                  <a href="/terms" className="hover:text-gray-700 transition-colors">{t('common:footer.termsOfService')}</a>
                  <span>|</span>
                  <a href="#" className="hover:text-gray-700 transition-colors">{t('common:footer.privacy')}</a>
                  <span>|</span>
                  <a href="mailto:mu9347mu@gmail.com?subject=Contact Us - timer.today&body=Hello timer.today team,%0D%0A%0D%0AI would like to..." className="hover:text-gray-700 transition-colors">{t('common:footer.contactUs')}</a>
                  <span>|</span>
                  <span>{t('common:footer.copyright')}</span>
                  <span className="lg:hidden">|</span>
                  <div className="lg:hidden">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
            </div>

            {/* Vertical divider - only visible on large screens */}
            <div className="hidden lg:flex w-px bg-gray-300 mx-6 h-40 self-center"></div>

            {/* Right side - Multilingual content - only show on desktop */}
            <div className="hidden lg:block lg:w-1/3">
              <div className="space-y-3 text-sm text-gray-600 mt-12">
                <p>timer countdown pomodoro focus work break</p>
                <p>计时器 倒计时 番茄钟 专注 休息</p>
                <p>タイマー カウントダウン ポモドーロ 集中 休憩</p>
                <p>temporizador cuenta regresiva pomodoro concentración</p>
                <p>Timer Countdown Pomodoro Konzentration Pause</p>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* Timer Completion Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center">
            <div className="mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">{t('common:message.timerComplete')}</h3>
              <p className="text-gray-600">{t('common:message.timerCompleteBody')}</p>
            </div>
            <Button onClick={handleCompletionModalClose} className="w-full">
              {t('common:button.ok')}
            </Button>
          </div>
        </div>
      )}

    </div>
  );
}
